import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  TextField,
  IconButton,
} from '@mui/material';
import { Save, Refresh, Psychology, Warning, Cancel, Drafts, ExpandMore, ExpandLess, Search as SearchIcon } from '@mui/icons-material';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';

const DRAFT_KEY_PREFIX = 'master_screen_draft_';

interface SchemaSection {
  id: string;
  name: string;
  fields?: Array<{ id: string; name?: string; type?: string }>;
  groups?: Array<{ id: string; name: string; fields: Array<{ id: string; name?: string; type?: string }> }>;
}

interface MappingRow {
  section_id: string;
  section_name: string;
  field_id: string;
  field_name: string;
  target_type: string;
  program_id: string;
  stage_id: string;
  data_element_id: string;
  parent_group_id?: string;
  parent_group_name?: string;
}

const TARGET_TYPES = ['DHIS2 Data Element', 'TEI Attribute', 'Internal', 'Custom', 'webhook', 'FHIR'];

/** Tree node for section → group → field. Field entries keep index into mappings for updateMapping. */
interface TreeSection {
  sectionId: string;
  sectionName: string;
  standalone: { row: MappingRow; index: number }[];
  groups: { groupId: string; groupName: string; fields: { row: MappingRow; index: number }[] }[];
}

function matchSearch(row: MappingRow, q: string): boolean {
  if (!q.trim()) return true;
  const lower = q.toLowerCase().trim();
  const parts = [
    row.section_id,
    row.section_name,
    row.parent_group_id ?? '',
    row.parent_group_name ?? '',
    row.field_id,
    row.field_name,
  ];
  return parts.some((p) => p && p.toLowerCase().includes(lower));
}

interface MasterScreenProps {
  configs: Dhis2ConfigOut[];
  selectedConfigId: string | null;
  onSelectConfigId: (id: string | null) => void;
  onOpenSynonyms?: (configId: string) => void;
}

export default function MasterScreen({
  configs,
  selectedConfigId,
  onSelectConfigId,
  onOpenSynonyms,
}: MasterScreenProps) {
  const [schema, setSchema] = useState<{ sections?: SchemaSection[] } | null>(null);
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceAction, setSourceAction] = useState<'none' | 'pull' | 'programdata' | 'admin'>('none');
  const [driftStatus, setDriftStatus] = useState<{ needs_review: boolean; schema_drift_detected_at: string | null; metadata_fetched_at: string | null } | null>(null);
  const [refreshingMetadata, setRefreshingMetadata] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const buildRowsFromSchema = (s: { sections?: SchemaSection[] }, currentMappings: MappingRow[]): MappingRow[] => {
    const sections = s?.sections ?? [];
    const byKey = new Map<string, MappingRow>();
    for (const row of currentMappings) {
      byKey.set(`${row.section_id}\t${row.field_id}`, row);
    }
    const out: MappingRow[] = [];
    for (const sec of sections) {
      const sectionId = sec.id ?? '';
      const sectionName = sec.name ?? sectionId;
      const fields = sec.fields ?? [];
      for (const f of fields) {
        const fieldId = f.id ?? '';
        const fieldName = f.name ?? fieldId;
        const key = `${sectionId}\t${fieldId}`;
        const existing = byKey.get(key);
        out.push({
          section_id: sectionId,
          section_name: sectionName,
          field_id: fieldId,
          field_name: fieldName,
          target_type: existing?.target_type ?? 'DHIS2 Data Element',
          program_id: existing?.program_id ?? '',
          stage_id: existing?.stage_id ?? '',
          data_element_id: existing?.data_element_id ?? '',
          parent_group_id: undefined,
          parent_group_name: undefined,
        });
      }
      const groups = sec.groups ?? [];
      for (const grp of groups) {
        const groupId = grp.id ?? '';
        const groupName = grp.name ?? groupId;
        for (const f of grp.fields ?? []) {
          const fieldId = f.id ?? '';
          const fieldName = f.name ?? fieldId;
          const key = `${sectionId}\t${fieldId}`;
          const existing = byKey.get(key);
          out.push({
            section_id: sectionId,
            section_name: sectionName,
            field_id: fieldId,
            field_name: fieldName,
            target_type: existing?.target_type ?? 'DHIS2 Data Element',
            program_id: existing?.program_id ?? '',
            stage_id: existing?.stage_id ?? '',
            data_element_id: existing?.data_element_id ?? '',
            parent_group_id: groupId,
            parent_group_name: groupName,
          });
        }
      }
    }
    return out;
  };

  const loadSchema = async () => {
    if (!selectedConfigId) {
      setSchema(null);
      setMappings([]);
      return;
    }
    setLoadingSchema(true);
    setError(null);
    try {
      const [schemaRes, mapRes] = await Promise.all([
        dhis2ConfigAPI.getSchema(selectedConfigId),
        dhis2ConfigAPI.getFieldMappings(selectedConfigId),
      ]);
      const data = (schemaRes.data ?? {}) as { sections?: SchemaSection[] };
      setSchema(data);
      const mapData = mapRes.data as Record<string, unknown> | undefined;
      const list = Array.isArray(mapData?.field_mappings)
        ? (mapData.field_mappings as Array<Record<string, string>>)
        : Array.isArray(mapData)
          ? (mapData as Array<Record<string, string>>)
          : [];
      const current: MappingRow[] = list.map((m: Record<string, string>) => ({
        section_id: (m.section_id as string) ?? '',
        section_name: (m.section_name as string) ?? '',
        field_id: (m.field_id as string) ?? '',
        field_name: (m.field_name as string) ?? '',
        target_type: (m.target_type as string) ?? 'DHIS2 Data Element',
        program_id: (m.program_id as string) ?? '',
        stage_id: (m.stage_id as string) ?? '',
        data_element_id: (m.data_element_id as string) ?? '',
        parent_group_id: (m.parent_group_id as string) || undefined,
        parent_group_name: (m.parent_group_name as string) || undefined,
      }));
      setMappings(buildRowsFromSchema(data, current));
      try { sessionStorage.removeItem(DRAFT_KEY_PREFIX + selectedConfigId); } catch { /* ignore */ }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load schema');
      setSchema(null);
      setMappings([]);
    } finally {
      setLoadingSchema(false);
    }
  };

  const handlePullFromDhis2 = async () => {
    if (!selectedConfigId) return;
    setLoadingSchema(true);
    setError(null);
    try {
      const res = await dhis2ConfigAPI.getMetadata(selectedConfigId);
      const wrap = res.data as { success?: boolean; data?: Record<string, unknown> };
      const data = wrap?.data ?? wrap;
      if (data && typeof data === 'object' && 'sections' in data) {
        await dhis2ConfigAPI.putSchema(selectedConfigId, data as object);
        setSchema(data as { sections?: SchemaSection[] });
        setMappings(buildRowsFromSchema(data as { sections?: SchemaSection[] }, mappings));
      } else {
        setError('Metadata did not return a unified schema (sections). Configure metadata endpoint to return transformed schema.');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Pull from DHIS2 failed');
    } finally {
      setLoadingSchema(false);
      if (selectedConfigId) loadDriftStatus();
    }
  };

  const handleLoadProgramData = async () => {
    if (!selectedConfigId) return;
    setLoadingSchema(true);
    setError(null);
    try {
      const res = await dhis2ConfigAPI.getProgramData(selectedConfigId);
      const wrap = res.data as { success?: boolean; schema?: Record<string, unknown> };
      const data = wrap?.schema ?? res.data;
      if (data && typeof data === 'object' && 'sections' in data) {
        await dhis2ConfigAPI.putSchema(selectedConfigId, data as object);
        setSchema(data as { sections?: SchemaSection[] });
        setMappings(buildRowsFromSchema(data as { sections?: SchemaSection[] }, mappings));
      } else {
        setError('ProgramData did not return a schema with sections.');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load programData failed');
    } finally {
      setLoadingSchema(false);
    }
  };

  const loadDriftStatus = async () => {
    if (!selectedConfigId) {
      setDriftStatus(null);
      return;
    }
    try {
      const res = await dhis2ConfigAPI.getSchemaDrift(selectedConfigId);
      setDriftStatus(res.data);
    } catch {
      setDriftStatus(null);
    }
  };

  const handleRefreshMetadata = async () => {
    if (!selectedConfigId) return;
    setRefreshingMetadata(true);
    setError(null);
    try {
      await dhis2ConfigAPI.refreshMetadata(selectedConfigId);
      await loadDriftStatus();
      await loadSchema();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Refresh metadata failed');
    } finally {
      setRefreshingMetadata(false);
    }
  };

  useEffect(() => {
    if (selectedConfigId) {
      setLoadingMappings(true);
      loadSchema().finally(() => setLoadingMappings(false));
      loadDriftStatus();
    } else {
      setSchema(null);
      setMappings([]);
      setDriftStatus(null);
    }
  }, [selectedConfigId]);

  // When search or mappings change, expand all sections and groups so hierarchy (or search results) is visible
  useEffect(() => {
    if (mappings.length === 0) return;
    const bySection = new Map<string, { groups: string[] }>();
    mappings.forEach((row) => {
      const sid = row.section_id;
      if (!bySection.has(sid)) bySection.set(sid, { groups: [] });
      if (row.parent_group_id && !bySection.get(sid)!.groups.includes(row.parent_group_id)) {
        bySection.get(sid)!.groups.push(row.parent_group_id);
      }
    });
    setExpandedSections(new Set(bySection.keys()));
    const groupKeys = new Set<string>();
    bySection.forEach((v, sid) => v.groups.forEach((gid) => groupKeys.add(`${sid}\t${gid}`)));
    setExpandedGroups(groupKeys);
  }, [searchText, mappings.length]);

  const handleSave = async () => {
    if (!selectedConfigId) return;
    setSaving(true);
    setError(null);
    try {
      const current = (await dhis2ConfigAPI.getFieldMappings(selectedConfigId)).data as Record<string, unknown> | undefined;
      const field_mappings = mappings.map((m) => ({
        section_id: m.section_id,
        section_name: m.section_name,
        field_id: m.field_id,
        field_name: m.field_name,
        target_type: m.target_type,
        program_id: m.program_id,
        stage_id: m.stage_id,
        data_element_id: m.data_element_id,
        ...(m.parent_group_id && { parent_group_id: m.parent_group_id }),
        ...(m.parent_group_name && { parent_group_name: m.parent_group_name }),
      }));
      await dhis2ConfigAPI.putFieldMappings(selectedConfigId, { ...(current ?? {}), field_mappings });
      try { sessionStorage.removeItem(DRAFT_KEY_PREFIX + selectedConfigId); } catch { /* ignore */ }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => {
    if (!selectedConfigId || mappings.length === 0) return;
    try {
      sessionStorage.setItem(DRAFT_KEY_PREFIX + selectedConfigId, JSON.stringify(mappings));
    } catch {
      setError('Failed to save draft');
    }
  };

  const handleLoadDraft = () => {
    if (!selectedConfigId) return;
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY_PREFIX + selectedConfigId);
      if (raw) {
        const list = JSON.parse(raw) as MappingRow[];
        if (Array.isArray(list)) setMappings(list);
      }
    } catch {
      setError('Failed to load draft');
    }
  };

  const handleCancel = () => {
    loadSchema();
  };

  const updateMapping = (index: number, field: keyof MappingRow, value: string | boolean) => {
    setMappings((prev) => {
      const next = [...prev];
      const row = next[index];
      if (row && field in row) (row as unknown as Record<string, string | boolean>)[field] = value;
      return next;
    });
  };

  const selectedConfig = configs.find((c) => c.id === selectedConfigId);
  const canPull = selectedConfig?.schema_source === 'dhis2_metadata';
  const canProgramData = selectedConfig?.schema_source === 'dhis2_program_data_json';

  /** Build tree: section → standalone fields + groups → fields. Each field keeps index into mappings. */
  const treeSections = useMemo(() => {
    const bySection = new Map<string, { sectionId: string; sectionName: string; standalone: { row: MappingRow; index: number }[]; groups: Map<string, { groupId: string; groupName: string; fields: { row: MappingRow; index: number }[] }> }>();
    mappings.forEach((row, index) => {
      if (searchText.trim() && !matchSearch(row, searchText)) return;
      const sid = row.section_id;
      if (!bySection.has(sid)) {
        bySection.set(sid, {
          sectionId: sid,
          sectionName: row.section_name,
          standalone: [],
          groups: new Map(),
        });
      }
      const sec = bySection.get(sid)!;
      const entry = { row, index };
      if (row.parent_group_id && row.parent_group_name) {
        if (!sec.groups.has(row.parent_group_id)) {
          sec.groups.set(row.parent_group_id, { groupId: row.parent_group_id, groupName: row.parent_group_name, fields: [] });
        }
        sec.groups.get(row.parent_group_id)!.fields.push(entry);
      } else {
        sec.standalone.push(entry);
      }
    });
    const sections: TreeSection[] = [];
    bySection.forEach((sec) => {
      sections.push({
        sectionId: sec.sectionId,
        sectionName: sec.sectionName,
        standalone: sec.standalone,
        groups: Array.from(sec.groups.values()),
      });
    });
    return sections;
  }, [mappings, searchText]);

  /** Flatten tree into a single list of rows for one table (fixes alignment). */
  type FlatRow =
    | { type: 'section'; sectionId: string; sectionName: string }
    | { type: 'standalone-header' }
    | { type: 'field'; row: MappingRow; index: number; indent: number }
    | { type: 'group'; groupId: string; groupName: string; sectionId: string; sectionName: string }
    | { type: 'group-field'; row: MappingRow; index: number; indent: number };
  const flatRows = useMemo((): FlatRow[] => {
    const out: FlatRow[] = [];
    treeSections.forEach((sec) => {
      const secExpanded = expandedSections.has(sec.sectionId);
      out.push({ type: 'section', sectionId: sec.sectionId, sectionName: sec.sectionName });
      if (!secExpanded) return;
      if (sec.standalone.length > 0) {
        out.push({ type: 'standalone-header' });
        sec.standalone.forEach(({ row, index }) => out.push({ type: 'field', row, index, indent: 1 }));
      }
      sec.groups.forEach((grp) => {
        const grpKey = `${sec.sectionId}\t${grp.groupId}`;
        const grpExpanded = expandedGroups.has(grpKey);
        out.push({ type: 'group', groupId: grp.groupId, groupName: grp.groupName, sectionId: sec.sectionId, sectionName: sec.sectionName });
        if (grpExpanded) {
          grp.fields.forEach(({ row, index }) => out.push({ type: 'group-field', row, index, indent: 2 }));
        }
      });
    });
    return out;
  }, [treeSections, expandedSections, expandedGroups]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={selectedConfigId ?? ''}
            label="Config"
            onChange={(e) => onSelectConfigId(e.target.value || null)}
          >
            <MenuItem value="">— Select config —</MenuItem>
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          disabled={!canPull || loadingSchema}
          onClick={handlePullFromDhis2}
        >
          {loadingSchema ? 'Loading…' : 'Pull from DHIS2'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!canProgramData || loadingSchema}
          onClick={handleLoadProgramData}
        >
          {loadingSchema ? 'Loading…' : 'Load programData'}
        </Button>
        <Button size="small" variant="outlined" disabled={!selectedConfigId || loadingSchema} onClick={loadSchema}>
          Refresh
        </Button>
        <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={!selectedConfigId || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outlined" size="small" startIcon={<Drafts />} onClick={handleSaveDraft} disabled={!selectedConfigId || mappings.length === 0}>
          Draft
        </Button>
        <Button variant="outlined" size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={!selectedConfigId || loadingSchema}>
          Cancel
        </Button>
        {selectedConfigId && typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DRAFT_KEY_PREFIX + selectedConfigId) != null && (
          <Button size="small" color="secondary" onClick={handleLoadDraft}>
            Restore draft
          </Button>
        )}
        {onOpenSynonyms && selectedConfigId && (
          <Button size="small" startIcon={<Psychology />} onClick={() => onOpenSynonyms(selectedConfigId)}>
            Suggest synonyms for all
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {selectedConfigId && driftStatus?.needs_review && (
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefreshMetadata} disabled={refreshingMetadata}>
              {refreshingMetadata ? 'Refreshing…' : 'Refresh metadata'}
            </Button>
          }
        >
          Schema has changed in DHIS2 since last refresh. Review and click &quot;Refresh metadata&quot; to update.
        </Alert>
      )}

      {!selectedConfigId ? (
        <Typography color="text.secondary">Select a config to load schema and edit field mappings.</Typography>
      ) : loadingMappings && mappings.length === 0 ? (
        <Typography>Loading…</Typography>
      ) : mappings.length === 0 ? (
        <Typography color="text.secondary">
          No schema or fields. Use Pull from DHIS2 / Load programData for this config, or define schema in Form Schema Editor.
        </Typography>
      ) : (
        <Box>
          <TextField
            size="small"
            placeholder="Search by section, group, or field name/ID…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
            sx={{ mb: 1.5, maxWidth: 400 }}
            fullWidth
          />
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 620 }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell sx={{ minWidth: 120 }}>Section</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Group (parent DE)</TableCell>
                  <TableCell sx={{ minWidth: 100, fontFamily: 'monospace', fontSize: '0.75rem' }}>Group ID</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Field</TableCell>
                  <TableCell sx={{ minWidth: 100, fontFamily: 'monospace', fontSize: '0.75rem' }}>Field ID</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Target type</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Data Element / Attribute</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flatRows.map((r, idx) => {
                  if (r.type === 'section') {
                    const secExpanded = expandedSections.has(r.sectionId);
                    return (
                      <TableRow key={`sec-${r.sectionId}`} hover sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ pl: 1 }}>
                          <IconButton size="small" onClick={() => toggleSection(r.sectionId)} aria-label={secExpanded ? 'Collapse' : 'Expand'}>
                            {secExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{r.sectionName}</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>—</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>—</TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    );
                  }
                  if (r.type === 'standalone-header') {
                    return (
                      <TableRow key={`standalone-${idx}`} sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ pl: 2 }} />
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Standalone fields</TableCell>
                        <TableCell colSpan={2}>—</TableCell>
                        <TableCell colSpan={4} />
                      </TableRow>
                    );
                  }
                  if (r.type === 'field') {
                    const { row, index } = r;
                    return (
                      <TableRow key={`${row.section_id}-${row.field_id}`}>
                        <TableCell sx={{ pl: 3 }} />
                        <TableCell>{row.section_name}</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>—</TableCell>
                        <TableCell>{row.field_name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.field_id}</TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select value={row.target_type} onChange={(e) => updateMapping(index, 'target_type', e.target.value)}>
                              {TARGET_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth value={row.data_element_id} onChange={(e) => updateMapping(index, 'data_element_id', e.target.value)} placeholder="data element id" />
                        </TableCell>
                      </TableRow>
                    );
                  }
                  if (r.type === 'group') {
                    const grpKey = `${r.sectionId}\t${r.groupId}`;
                    const grpExpanded = expandedGroups.has(grpKey);
                    return (
                      <TableRow key={grpKey} sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ pl: 2 }}>
                          <IconButton size="small" onClick={() => toggleGroup(grpKey)} aria-label={grpExpanded ? 'Collapse group' : 'Expand group'}>
                            {grpExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{r.sectionName}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{r.groupName}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.groupId}</TableCell>
                        <TableCell colSpan={2}>—</TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    );
                  }
                  // group-field
                  const { row, index } = r;
                  return (
                    <TableRow key={`${row.section_id}-${row.field_id}`}>
                      <TableCell sx={{ pl: 4 }} />
                      <TableCell>{row.section_name}</TableCell>
                      <TableCell>{row.parent_group_name ?? '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.parent_group_id ?? '—'}</TableCell>
                      <TableCell>{row.field_name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.field_id}</TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select value={row.target_type} onChange={(e) => updateMapping(index, 'target_type', e.target.value)}>
                            {TARGET_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth value={row.data_element_id} onChange={(e) => updateMapping(index, 'data_element_id', e.target.value)} placeholder="data element id" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
